import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDate, fmtMoney, fmtErr } from "../../api";
import Layout from "../../components/Layout";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_LABEL = {
  clarifying: "Draft", submitted: "With operator", awaiting_approval: "Needs your approval",
  approved: "Scheduled", completed: "Done", declined: "Declined",
};

export default function OwnerRequests() {
  const [approvals, setApprovals] = useState(null);
  const [items, setItems] = useState([]);

  const load = () => {
    api.get("/owner/approvals").then((r) => setApprovals(r.data));
    api.get("/instructions").then((r) => setItems(r.data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load by design
  useEffect(() => { load(); }, []);

  const respond = async (id, action) => {
    try {
      await api.post(`/instructions/${id}/${action}`);
      toast.success(action === "approve" ? "Approved — it's on the schedule" : "Declined");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!approvals) return <Layout title="Requests"><p className="text-muted-foreground">Loading…</p></Layout>;

  return (
    <Layout title="Requests">
      <Link to="/owner/requests/new" data-testid="new-request-btn">
        <div className="mb-6 flex h-14 items-center justify-center gap-2 rounded-md bg-primary font-bold text-primary-foreground">
          <Plus size={20} weight="bold" /> New request
        </div>
      </Link>

      {approvals.length > 0 && (
        <section className="mb-8">
          <p className="label-caps mb-3">Waiting on you</p>
          <div className="space-y-3" data-testid="approval-list">
            {approvals.map((a) => (
              <div key={a.id} className="card-flat space-y-3 border-l-4 border-l-[hsl(43,74%,50%)] p-4" data-testid={`approval-${a.id}`}>
                <p className="font-semibold">{a.property_name}</p>
                <p className="text-sm">{a.description}</p>
                {a.qa?.length > 0 && (
                  <div className="rounded-md bg-muted p-3 text-xs space-y-1">
                    {a.qa.map((qa) => <p key={qa.q}><span className="font-semibold">{qa.q}</span> — {qa.a}</p>)}
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="font-heading text-xl font-bold">{fmtMoney(a.quoted_cost)}</span>
                  <span className="text-muted-foreground">{a.mode === "standalone" ? "ASAP visit" : `Next visit${a.visit_date ? ` · ${fmtDate(a.visit_date)}` : ""}`}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid={`approve-${a.id}`} onClick={() => respond(a.id, "approve")} className="h-14 rounded-md bg-primary font-bold text-primary-foreground">Approve</button>
                  <button data-testid={`decline-${a.id}`} onClick={() => respond(a.id, "decline")} className="h-14 rounded-md border border-destructive font-bold text-destructive">Decline</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <p className="label-caps mb-3">All requests</p>
        <div className="space-y-2" data-testid="request-list">
          {items.length === 0 && <p className="card-flat p-6 text-sm text-muted-foreground">No requests yet.</p>}
          {items.map((i) => (
            <div key={i.id} className="card-flat flex items-center justify-between p-4" data-testid={`request-${i.id}`}>
              <div className="min-w-0">
                <p className="truncate font-semibold">{i.description}</p>
                <p className="text-sm text-muted-foreground">{i.property_name} {i.quoted_cost != null ? `· ${fmtMoney(i.quoted_cost)}` : ""}</p>
              </div>
              <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${i.status === "awaiting_approval" ? "bg-[hsl(43,74%,50%)] text-[hsl(240,10%,10%)]" : i.status === "approved" || i.status === "completed" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                {STATUS_LABEL[i.status]}
              </span>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}
